import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Globe, Lock, Eye, HelpCircle } from 'lucide-react';
import { RFQVisibilityType } from '@/lib/types/job';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';

interface RFQVisibilitySelectorProps {
  value: RFQVisibilityType | undefined;
  onChange: (value: RFQVisibilityType) => void;
  disabled?: boolean;
  vendorCount?: number;
}

export function RFQVisibilitySelector({ 
  value, 
  onChange, 
  disabled = false,
  vendorCount = 0
}: RFQVisibilitySelectorProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          RFQ Visibility
          {!value && (
            <Badge variant="destructive" className="ml-auto">Required</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TooltipProvider>
          <RadioGroup 
            value={value || ''} 
            onValueChange={(v) => onChange(v as RFQVisibilityType)}
            disabled={disabled}
            className="space-y-3"
          >
          {/* Public Option */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="public" id="public" />
            <Label htmlFor="public" className="flex items-center gap-2 font-medium">
              <Globe className="h-4 w-4 text-blue-500" />
              Public RFQ
              <Badge variant="outline" className="text-xs">
                Open to All
              </Badge>
            </Label>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button 
                  type="button" 
                  className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-sm">
                <div className="space-y-2">
                  <p className="text-sm">
                    All vendors who have completed enrollment can see and bid on this RFQ. You can optionally add match criteria 
                    to filter by documented capabilities, but all bids will be accepted.
                  </p>
                  <div className="flex flex-col gap-1 text-xs">
                    <span className="flex items-center gap-1 text-green-600">
                      ✓ Maximum vendor exposure
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      ✓ Competitive pricing
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      ✓ Faster vendor discovery
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Private Option */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="private" id="private" />
            <Label htmlFor="private" className="flex items-center gap-2 font-medium">
              <Lock className="h-4 w-4 text-orange-500" />
              Private RFQ
              <Badge variant="outline" className="text-xs">
                Invitation Only
              </Badge>
              {value === 'private' && vendorCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {vendorCount} vendor{vendorCount !== 1 ? 's' : ''} selected
                </Badge>
              )}
            </Label>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button 
                  type="button" 
                  className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-sm">
                <div className="space-y-2">
                  <p className="text-sm">
                    Only vendors you specifically invite can see and bid on this RFQ. 
                    Best for confidential projects or when working with known vendors.
                  </p>
                  <div className="flex flex-col gap-1 text-xs">
                    <span className="flex items-center gap-1 text-blue-600">
                      ✓ Controlled vendor access
                    </span>
                    <span className="flex items-center gap-1 text-blue-600">
                      ✓ Targeted invitations
                    </span>
                    <span className="flex items-center gap-1 text-blue-600">
                      ✓ Enhanced security
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </RadioGroup>
        </TooltipProvider>

      </CardContent>
    </Card>
  );
}
